import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Copy, Check, Mail } from 'lucide-react';
import { useAccessChecks } from '../components/status/useAccessChecks';
import { usePageMeta } from '../lib/usePageMeta';
import siteContent from '../content/site/content.json';

const API = process.env.REACT_APP_BACKEND_URL;
const CONTACT = siteContent.contact.email;

const browserLabel = () => {
  const ua = navigator.userAgent;
  const m = ua.match(/(Edg|Chrome|Firefox|Safari|OPR)\/([\d.]+)/g) || [];
  const b = m.find((x) => x.startsWith('Edg')) || m.find((x) => x.startsWith('OPR')) || m.find((x) => x.startsWith('Firefox')) || m.find((x) => x.startsWith('Chrome')) || m[0] || 'browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : 'unknown OS';
  return `${b.replace('Edg/', 'Edge ').replace('OPR/', 'Opera ').replace('/', ' ').replace(/\.\d+.*$/, '')} / ${os}`;
};

const StatusRow = ({ r }) => (
  <li className="flex items-start gap-3 py-3 border-b border-black/5 last:border-0" data-testid={`status-check-${r.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    {r.status === 'pending' && <Loader2 className="w-5 h-5 mt-0.5 shrink-0 animate-spin text-[#8A8078]" aria-label="checking" />}
    {r.status === 'ok' && <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#2F7A4A]" aria-label="passed" />}
    {r.status === 'fail' && <XCircle className="w-5 h-5 mt-0.5 shrink-0 text-[#B5541B]" aria-label="failed" />}
    <div className="min-w-0">
      <p className="font-semibold text-[#1F1E1E]">{r.name}</p>
      {r.detail && <p className="text-sm text-[#5B5551] break-words">{r.detail}</p>}
    </div>
    <span className={`ml-auto text-xs font-bold uppercase tracking-wide ${r.status === 'fail' ? 'text-[#B5541B]' : r.status === 'ok' ? 'text-[#2F7A4A]' : 'text-[#8A8078]'}`}>
      {r.status === 'pending' ? 'checking' : r.status === 'ok' ? 'OK' : 'failed'}
    </span>
  </li>
);

const StatusPage = () => {
  usePageMeta('Access check — ResiAX', 'Confirm that resiax.com and its API load correctly from your network.');
  const { results, done } = useAccessChecks();
  const [copied, setCopied] = useState(false);
  const [org, setOrg] = useState('');
  const reported = useRef(false);
  const failed = results.filter((r) => r.status === 'fail');

  const summary = useMemo(() => {
    const stamp = new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const parts = results.map((r) => `${r.name}: ${r.status === 'ok' ? 'OK' : r.status === 'fail' ? `FAILED (${r.detail})` : 'not run'}`);
    return `ResiAX access check — ${stamp}\n${parts.join('\n')}\nBrowser: ${browserLabel()}\nPage: ${window.location.href}`;
  }, [results]);

  useEffect(() => {
    if (!done || reported.current || failed.length === 0) return;
    reported.current = true;
    fetch(`${API}/api/status/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        checks: results.map((r) => ({ name: r.name, ok: r.status === 'ok', detail: r.detail || null })),
        user_agent: browserLabel(),
        page_protocol: window.location.protocol,
        organisation: org.trim() || null,
      }),
    }).catch(() => {});
  }, [done, failed.length, results, org]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(summary); } catch { window.prompt('Copy this report', summary); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const mailto = `mailto:${CONTACT}?subject=${encodeURIComponent('ResiAX access check result')}&body=${encodeURIComponent(summary + (org ? `\nOrganisation: ${org}` : ''))}`;

  return (
    <main className="min-h-screen bg-[#FFFAF8] text-[#1F1E1E] px-5 py-12" style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' }} data-testid="status-page">
      <div className="max-w-xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-widest text-[#B5541B] mb-2">ResiAX</p>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="status-title">Access check</h1>
        <p className="text-[#5B5551] mb-8">
          This page runs a few quick tests to confirm resiax.com works from your network. Nothing is installed and no personal data is collected.
        </p>

        <ul className="bg-white rounded-2xl border border-black/5 px-5 shadow-sm" data-testid="status-checks">
          {results.map((r) => <StatusRow key={r.name} r={r} />)}
        </ul>

        {done && (
          <div className={`mt-6 rounded-2xl p-5 ${failed.length ? 'bg-[#B5541B]/10 border border-[#B5541B]/30' : 'bg-[#2F7A4A]/10 border border-[#2F7A4A]/30'}`} data-testid="status-verdict">
            <p className="font-semibold">
              {failed.length ? `${failed.length} check${failed.length > 1 ? 's' : ''} failed.` : 'Everything works from your network.'}
            </p>
            <p className="text-sm text-[#5B5551] mt-1">
              {failed.length
                ? 'Please copy this report and send it to your IT team or to us — it tells them exactly what to allow.'
                : 'If ResiAX still seems broken for you, send us the report below and we will take a look.'}
            </p>
          </div>
        )}

        <div className="mt-6">
          <label className="block text-sm font-medium mb-1" htmlFor="status-org">Your organisation (optional)</label>
          <input id="status-org" data-testid="status-org-input" value={org} onChange={(e) => setOrg(e.target.value)} maxLength={120} placeholder="e.g. Riverside Housing" className="w-full rounded-xl border border-black/10 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#B5541B]/40" />
        </div>

        <pre className="mt-4 bg-[#1F1E1E] text-[#F5EDE6] text-xs rounded-2xl p-4 whitespace-pre-wrap break-words" data-testid="status-summary">{summary}{org ? `\nOrganisation: ${org}` : ''}</pre>

        <div className="mt-4 flex flex-wrap gap-3">
          <button type="button" onClick={copy} data-testid="status-copy-button" className="inline-flex items-center gap-2 bg-[#1F1E1E] text-white font-semibold px-5 py-2.5 rounded-full text-sm">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy report'}
          </button>
          <a href={mailto} data-testid="status-email-button" className="inline-flex items-center gap-2 bg-[#B5541B] text-white font-semibold px-5 py-2.5 rounded-full text-sm">
            <Mail className="w-4 h-4" /> Email report to ResiAX
          </a>
        </div>

        <div className="mt-10 text-sm text-[#5B5551] space-y-2" data-testid="status-it-note">
          <p className="font-semibold text-[#1F1E1E]">For IT teams</p>
          <p>
            Please allow <code className="bg-black/5 px-1 rounded">resiax.com</code> and <code className="bg-black/5 px-1 rounded">*.resiax.com</code> (category: Business / Technology; HTTPS only; hosted on Cloudflare). The site makes no third-party requests — fonts, scripts, images and the API (<code className="bg-black/5 px-1 rounded">/api/*</code>) are all served from resiax.com.
            Security contact: <a href="/.well-known/security.txt" className="underline">/.well-known/security.txt</a>.
          </p>
          <p>If this page did not load at all for a colleague, the whole domain is being blocked by category — the allow-list request above resolves that.</p>
        </div>

        <p className="mt-10 text-sm"><Link to="/" className="text-[#B5541B] font-semibold underline" data-testid="status-home-link">Back to resiax.com</Link></p>
      </div>
    </main>
  );
};

export default StatusPage;
